/**
 * LessonPanel.jsx
 * lesson.js의 렌더링 로직을 React 컴포넌트로 1:1 이관
 * 
 * 원본 함수 매핑:
 * - renderCurrentStep() → LessonPanel 컴포넌트
 * - renderConceptStep() → ConceptStep
 * - renderVisualLogicStep() → VisualLogicStep
 * - renderMasterPatternStep() → MasterPatternStep
 * - renderMirrorExampleStep() → MirrorExampleStep
 * - renderMcqStep() → McqStep
 * - renderFillInBlankStep() → FillInBlankStep
 * - renderFinalCodeStep() → FinalCodeStep
 */

import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import useLunaStore from '../../store/useLunaStore';
import useLunaFlow from '../../hooks/useLunaFlow';
import CodeEditor, { FillInBlankEditor } from '../editor/CodeEditor';
import MonacoCodeEditor from '../editor/MonacoCodeEditor'; // [New] Monaco for IME fix
import MonacoFillInBlankEditor from '../editor/MonacoFillInBlankEditor'; // [New] Monaco blanks
import api from '../../services/api';
import { AnimatePresence, motion } from 'framer-motion';
import { useRewardHandler } from '../sidebar/Sidebar'; // [Fix] Import missing hook
import ScanningLoader from '../ui/ScanningLoader'; // [New] Import
import ControlBar from './ControlBar'; // [Extracted] Shared ControlBar component
import { CodeFxStep, TerminalSimStep, DataGridStep, SafeRunnerStep } from './steps'; // [New] Interactive steps



// ============================================
// [New] Unified Language Detection Utility
// ============================================
function detectLanguage(code, explicitLanguage) {
  const explicit = explicitLanguage ? explicitLanguage.toLowerCase() : null;
  // If explicitly something other than js/python, trust it. 
  // But if it's 'javascript', we check if it's actually JSX.
  if (explicit && !['javascript', 'js', 'python', 'py'].includes(explicit)) {
    return explicit;
  }

  const c = String(code).trim();

  // 1. Explicit JSX/React Detection
  if (
    c.includes('import React') ||
    c.includes('className=') ||
    c.includes('htmlFor=') ||
    c.includes('useState') ||
    c.includes('useEffect') ||
    c.includes('<>') ||
    c.includes('</') || // Closing tag is a strong indicator
    (c.includes('const ') && c.includes('/>')) || // Self-closing tag
    (c.includes('const ') && c.includes(' = (') && c.includes('<')) // const E = ( <div...
  ) {
    return 'jsx';
  }

  // If explicit was javascript but didn't match JSX above, return javascript
  if (explicit === 'javascript' || explicit === 'js') return 'javascript';

  // 2. HTML Detection
  if (
    c.includes('<!DOCTYPE') ||
    c.includes('<html') ||
    c.includes('<body>') ||
    (c.includes('<div') && c.includes('</div>') && !c.includes('const ')) // HTML div but not inside JS var
  ) {
    return 'html';
  }

  // 3. CSS Detection
  if (
    c.includes('{') &&
    c.includes('}') &&
    c.includes(':') &&
    c.includes(';') &&
    !c.includes('function') &&
    !c.includes('=>') &&
    !c.includes('class ')
  ) {
    return 'css';
  }

  // 4. JavaScript Detection 
  // (If explicit='python' but looks like JS, we might want to override, 
  // but usually python default is for empty/unknown)
  if (
    c.includes('console.log') ||
    c.includes('const ') ||
    c.includes('let ') ||
    c.includes('function') ||
    c.includes('=>') ||
    c.includes('document.')
  ) {
    return 'javascript';
  }

  // 5. Python (Default fallback)
  return 'python';
}

function getDisplayFilename(language) {
  switch (language) {
    case 'python': return 'main.py';
    case 'javascript': return 'main.js';
    case 'jsx': return 'App.jsx';
    case 'html': return 'index.html';
    case 'css': return 'style.css';
    default: return `code.${language}`;
  }
}

// ============================================
// [New] Lesson Completion Card
// ============================================
function LessonCompletionCard({ onReview, onNext, isRoadmapMode, isLastLesson }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="h-full flex flex-col items-center justify-center p-8 bg-[#0B0E14] text-center"
    >
      <div className="w-24 h-24 mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-[0_0_30px_rgba(52,211,153,0.3)]">
        <i className="fas fa-check text-4xl text-white"></i>
      </div>

      <h2 className="text-3xl font-bold text-white mb-2">단원 학습 완료!</h2>
      <p className="text-slate-400 mb-8 max-w-md leading-relaxed">
        축하합니다! 이번 단원의 모든 학습 내용을 성공적으로 마쳤습니다.<br />
        이제 다음 단계로 나아갈 준비가 되셨나요?
      </p>

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
        <button
          onClick={onReview}
          className="flex-1 py-3 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 font-medium transition-all flex items-center justify-center gap-2 border border-white/5"
        >
          <i className="fas fa-undo text-xs"></i>
          레슨 복습하기
        </button>

        <button
          onClick={onNext}
          className="flex-1 py-3 px-4 rounded-lg bg-slate-200 text-slate-900 font-bold shadow-lg shadow-white/5 hover:shadow-white/10 transition-all flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-white"
        >
          {isRoadmapMode && isLastLesson ? (
            <>
              <span>다음 토픽으로</span>
              <i className="fas fa-map-marked-alt"></i>
            </>
          ) : (
            <>
              <span>다음 단원 시작</span>
              <i className="fas fa-arrow-right"></i>
            </>
          )}
        </button>
      </div>

      <div className="mt-8 p-4 rounded-lg bg-white/[0.02] border border-white/5 text-xs text-slate-500">
        <i className="fas fa-info-circle mr-1.5 text-cyan-500"></i>
        {isRoadmapMode && isLastLesson
          ? '로드맵의 다음 토픽이 잠금 해제됩니다.'
          : '현재 커리큘럼의 다음 단계로 자동으로 이동합니다.'}
      </div>
    </motion.div>
  );
}

// ============================================
// 유틸리티 함수들 (lesson.js에서 이관)
// ============================================

/**
 * 코드 문자열 포매팅 - 주석/문자열 내 줄바꿈 보존 (다국어 지원)
 * 
 * JSON 파싱 후 주석/문자열 내부의 실제 줄바꿈(\n)을 감지하여
 * 해당 컨텍스트에서는 리터럴 \n 텍스트로 표시합니다.
 * 
 * 지원 언어별 주석:
 * - Python: # (한줄)
 * - JavaScript/JSX: // (한줄), block comment (블록)
 * - HTML: html comment (블록)
 * - VBA/Excel: apostrophe (한줄), Rem (한줄)
 * - CSS: block comment (블록)
 */
function formatCodeString(text, language = 'python') {
  if (!text) return '';

  const str = String(text);
  const lang = (language || 'python').toLowerCase();

  let result = '';
  let i = 0;

  let inString = false;
  let stringDelimiter = ''; // ', ", """, '''
  let inLineComment = false;
  let inBlockComment = false;
  let blockCommentEnd = '';

  while (i < str.length) {
    const char = str[i];
    const next2 = str.slice(i, i + 2);
    const next3 = str.slice(i, i + 3);
    const next4 = str.slice(i, i + 4);

    // 1. Literal \n Handling (Convert to Actual Newline unless in string)
    if (next2 === '\\n') {
      if (inString) {
        result += '\\n';
      } else {
        result += '\n';
        inLineComment = false;
      }
      i += 2;
      continue;
    }

    // 2. Actual Newline
    if (char === '\n') {
      result += '\n';
      inLineComment = false;
      i++;
      continue;
    }

    // 3. Block Comment End
    if (inBlockComment) {
      if (str.slice(i, i + blockCommentEnd.length) === blockCommentEnd) {
        result += blockCommentEnd;
        inBlockComment = false;
        i += blockCommentEnd.length;
        continue;
      }
      result += char;
      i++;
      continue;
    }

    // 4. String End / Escape
    if (inString) {
      if (char === '\\') { // Escape char in string
        result += char;
        if (i + 1 < str.length) {
          result += str[i + 1];
          i++;
        }
        i++;
        continue;
      }
      if (str.slice(i, i + stringDelimiter.length) === stringDelimiter) {
        inString = false;
        result += stringDelimiter;
        i += stringDelimiter.length;
        continue;
      }
      result += char;
      i++;
      continue;
    }

    // 5. Block Comment Start
    if (!inLineComment) {
      if (next2 === '/*' && ['javascript', 'js', 'jsx', 'css', 'c', 'cpp', 'java'].includes(lang)) {
        inBlockComment = true;
        blockCommentEnd = '*/';
        result += next2;
        i += 2;
        continue;
      }
      if (next4 === '<!--' && ['html', 'htmlmixed', 'xml'].includes(lang)) {
        inBlockComment = true;
        blockCommentEnd = '-->';
        result += next4;
        i += 4;
        continue;
      }
    }

    // 6. Python Triple Quotes
    if (!inLineComment && ['python', 'py'].includes(lang)) {
      if (next3 === '"""' || next3 === "'''") {
        inString = true;
        stringDelimiter = next3;
        result += next3;
        i += 3;
        continue;
      }
    }

    // 7. Line Comment Start
    if (!inLineComment) {
      if (char === '#' && ['python', 'py', 'r', 'bash', 'shell'].includes(lang)) {
        inLineComment = true;
      } else if (next2 === '//' && ['javascript', 'js', 'jsx', 'c', 'cpp', 'java'].includes(lang)) {
        inLineComment = true;
        result += next2;
        i += 2;
        continue;
      } else if (char === "'" && ['vba', 'bas', 'excel'].includes(lang)) {
        inLineComment = true;
      }
    }

    // 8. String Start (Single/Double)
    if (!inLineComment) {
      if (char === '"' || char === "'") {
        inString = true;
        stringDelimiter = char;
        result += char;
        i++;
        continue;
      }
    }

    // 9. Regular Character
    result += char;
    i++;
  }

  return result;
}

/**
 * Mermaid 코드 위생 처리 (HTML 태그 제거 및 안전한 텍스트 변환)
 */
function sanitizeMermaidCode(text) {
  if (!text) return '';
  let code = formatCodeString(text);

  // 1. HTML 태그 제거 (<tag ...> 형태)
  code = code.replace(/<[^>]+>/g, (match) => {
    if (match.includes('--') || match === '<' || match === '>') return match;
    return '';
  });

  // 2. Remove [ ] artifacts (often generated by AI as checkboxes)
  // Removes lines that are just brackets, or brackets at start/end of lines causing issues
  code = code.replace(/^\s*\[\s*\]\s*$/gm, ''); // Remove lines that are just [ ]
  code = code.replace(/^\s*\[\s*\]/gm, '');     // Remove [ ] at start of lines (e.g. "[ ] graph TD")
  code = code.replace(/\[\s*\]/g, '');          // Remove inline [ ] artifacts entirely

  // 3. Remove hardcoded styles to enforce LUNA theme
  // (e.g., style A fill:#..., stroke:#...)
  code = code.replace(/^\s*style\s+[A-Za-z0-9_]+\s+fill:.*$/gm, '');

  // 4. [New] Nested Quotes Sanitization
  // Mermaid Node Text Quotes Handling
  const brackets = [
    { start: '["', end: '"]' },
    { start: '("', end: '")' },
    { start: '{{"', end: '"}}' },
    { start: '>"', end: '"]' }, // Odd shape
    { start: 'diam"', end: '"diam' }, // Custom shapes if any
    { start: '|"', end: '"|' } // Labels
  ];

  brackets.forEach(({ start, end }) => {
    // Treat escaped brackets
    const pattern = new RegExp(`(\\${start.split('').join('\\')})([\\s\\S]*?)(\\${end.split('').join('\\')})`, 'g');

    code = code.replace(pattern, (match, open, content, close) => {
      // Replace double quotes with single quotes inside content
      return open + content.replace(/"/g, "'") + close;
    });
  });

  return code;
}

/**
 * 코드 블록 내의 이스케이프 시퀀스를 보존하면서 \n을 줄바꿈으로 변환
 */
function preserveCodeEscapes(text) {
  if (!text) return text;

  const codeBlocks = [];
  let processed = text.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  const inlineCodes = [];
  processed = processed.replace(/`[^`]+`/g, (match) => {
    inlineCodes.push(match);
    return `__INLINE_CODE_${inlineCodes.length - 1}__`;
  });

  processed = formatCodeString(processed);

  inlineCodes.forEach((code, i) => {
    processed = processed.replace(`__INLINE_CODE_${i}__`, code);
  });

  codeBlocks.forEach((code, i) => {
    processed = processed.replace(`__CODE_BLOCK_${i}__`, code);
  });

  return processed;
}

/**
 * solution 객체로부터 빈칸 힌트 생성
 */
function generateBlankHints(solution, descriptionText) {
  if (!solution) return null;

  const hints = [];

  if (typeof solution === 'object' && !Array.isArray(solution)) {
    const keys = Object.keys(solution).sort();
    keys.forEach((key, idx) => {
      const value = solution[key];
      const typeHint = detectTypeHint(value, descriptionText);
      const blankNum = key.match(/(\d+)/)?.[1] || (idx + 1);
      hints.push({ blankNum, typeHint });
    });
  } else if (typeof solution === 'string' && solution.trim()) {
    const typeHint = detectTypeHint(solution, descriptionText);
    hints.push({ blankNum: 1, typeHint });
  }

  return hints.length > 0 ? hints : null;
}

/**
 * [Phase 1] 템플릿에서 사용자가 입력한 빈칸 값들을 추출
 * @param {string} userCode - 사용자가 작성한 전체 코드
 * @param {string} template - 원본 템플릿 (빈칸이 __BLANK__ 또는 ___ 등으로 표시)
 * @returns {string[]} 추출된 빈칸 값 배열
 */
function extractBlankValues(userCode, template) {
  if (!template || !userCode) return [];

  // 빈칸 패턴: __BLANK__, ___, ??? 등
  const blankPattern = /(__BLANK__|___|\?\?\?)/g;
  const blanks = template.match(blankPattern) || [];

  if (blanks.length === 0) return [];

  // 템플릿을 정규식 패턴으로 변환 (빈칸 → 캡쳐 그룹)
  let regexPattern = template
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // 특수문자 이스케이프
    .replace(/\\(__BLANK__\\|___\\|\\\?\\\?\\\?\\)/g, '(.+?)'); // 빈칸 → 캡쳐

  // 공백/줄바꿈 유연하게 매칭
  regexPattern = regexPattern.replace(/\\s+/g, '\\s*');

  try {
    const regex = new RegExp(regexPattern, 's');
    const match = userCode.match(regex);

    if (match) {
      // 첫 번째는 전체 매칭, 이후가 캡쳐 그룹
      return match.slice(1);
    }
  } catch (e) {
    console.warn('[extractBlankValues] Regex 생성 실패:', e);
  }

  return [];
}

/**
 * [Phase 1] solution과 사용자 입력 빠른 비교
 * @param {string|Object} solution - 정답 (문자열 또는 {blank_1: "값", blank_2: "값"} 형태)
 * @param {string} userCode - 사용자 코드
 * @param {string} template - 원본 템플릿
 * @returns {{ isCorrect: boolean, matchedBlanks?: number, totalBlanks?: number }}
 */
function quickCompareWithSolution(solution, userCode, template) {
  if (!solution) return { isCorrect: false };

  // 정규화 함수: 공백, 따옴표 유형 통일
  const normalize = (s) => {
    if (s === null || s === undefined) return '';
    return String(s)
      .trim()
      .replace(/\s+/g, ' ')           // 연속 공백 → 단일 공백
      .replace(/[""]/g, '"')          // 스마트 큰따옴표 → 일반 큰따옴표
      .replace(/['']/g, "'")          // 스마트 작은따옴표 → 일반 작은따옴표
      .replace(/["']/g, '"')          // 작은따옴표 → 큰따옴표 (통일)
      .toLowerCase();
  };

  // 자료형만 비교 (값은 무시) - 자유 입력 문제용
  const typeMatch = (expected, actual) => {
    const e = String(expected).trim();
    const a = String(actual).trim();

    // 정수형
    if (/^[-+]?\d+$/.test(e) && /^[-+]?\d+$/.test(a)) return true;
    // 실수형
    if (/^[-+]?\d+\.\d+$/.test(e) && /^[-+]?\d+\.\d+$/.test(a)) return true;
    // 문자열 (따옴표로 감싸진)
    if (/^["'].*["']$/.test(e) && /^["'].*["']$/.test(a)) return true;
    // 불리언
    if (/^(true|false)$/i.test(e) && /^(true|false)$/i.test(a)) return true;

    return false;
  };

  // Case 1: solution이 문자열인 경우 (단일 빈칸)
  if (typeof solution === 'string') {
    const normalizedSol = normalize(solution);
    const normalizedCode = normalize(userCode);

    // 정확히 포함되는지 또는 자료형만 맞는지
    const exactMatch = normalizedCode.includes(normalizedSol);
    const typeOnlyMatch = typeMatch(solution, userCode);

    return { isCorrect: exactMatch || typeOnlyMatch, matchedBlanks: 1, totalBlanks: 1 };
  }

  // Case 2: solution이 딕셔너리인 경우 (다중 빈칸)
  if (typeof solution === 'object' && !Array.isArray(solution)) {
    const solutionKeys = Object.keys(solution).sort();
    const extractedValues = extractBlankValues(userCode, template);

    // 추출 실패 시 전체 코드에서 값 포함 여부로 폴백
    if (extractedValues.length === 0) {
      let matchCount = 0;
      for (const key of solutionKeys) {
        const expectedVal = normalize(solution[key]);
        if (normalize(userCode).includes(expectedVal)) {
          matchCount++;
        }
      }
      return {
        isCorrect: matchCount === solutionKeys.length,
        matchedBlanks: matchCount,
        totalBlanks: solutionKeys.length
      };
    }

    // 추출된 값과 solution 비교
    let matchCount = 0;
    for (let i = 0; i < solutionKeys.length; i++) {
      const expectedVal = solution[solutionKeys[i]];
      const actualVal = extractedValues[i] || '';

      if (normalize(expectedVal) === normalize(actualVal) || typeMatch(expectedVal, actualVal)) {
        matchCount++;
      }
    }

    return {
      isCorrect: matchCount === solutionKeys.length,
      matchedBlanks: matchCount,
      totalBlanks: solutionKeys.length
    };
  }

  return { isCorrect: false };
}

/**
 * 값의 자료형을 분석하여 힌트 문자열 생성
 */
function detectTypeHint(value, descriptionText) {
  const v = String(value || '').trim();
  const hasSpecificValue = descriptionText.includes(v);

  if (/^[-+]?\d+$/.test(v)) {
    return hasSpecificValue ? `정수 ${v}` : '정수(int) 입력 (예: 20, 100 등)';
  }

  if (/^[-+]?\d+\.\d+$/.test(v)) {
    return hasSpecificValue ? `실수 ${v}` : '실수(float) 입력 (예: 3.14, 178.5 등)';
  }

  if (/^["'].*["']$/.test(v)) {
    return hasSpecificValue ? `문자열 ${v}` : '문자열(str) 입력 (예: "Hello" 등)';
  }

  if (/^(True|False)$/i.test(v)) {
    return '불리언(bool) 입력 (True 또는 False)';
  }

  if (/^\[.*\]$/.test(v)) {
    return '리스트(list) 입력 (예: [1, 2, 3])';
  }

  if (/^\{.*\}$/.test(v)) {
    return '딕셔너리(dict) 입력 (예: {"key": "value"})';
  }

  if (v.length < 50) {
    return `코드: ${v}`;
  }

  return '적절한 코드 입력';
}

/**
 * AI 피드백이 긍정적인지(정답인지) 판별하는 헬퍼 함수
 */
function isPositiveFeedback(text) {
  if (!text) return false;
  const positiveKeywords = [
    '정답', '성공', '잘했', '완벽', '훌륭', '맞았', '정확', '최고', '좋아', '대단',
    'good', 'correct', 'pass', 'excellent', 'perfect', 'great'
  ];
  return positiveKeywords.some(keyword => text.includes(keyword));
}

// ============================================
// IDE 스타일 코드 블록 컴포넌트
// ============================================
function IdeCodeBlock({ code, language = 'python', title }) {
  const displayTitle = title || getDisplayFilename(language);

  return (
    <div className="ide-code-block rounded-lg overflow-hidden bg-black/20 backdrop-blur-md border border-white/5 my-4">
      <div className="ide-header flex items-center gap-2 px-4 py-2 bg-white/5 border-b border-white/5">
        <div className="ide-dots flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-500/80" />
          <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <span className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <span className="ide-title text-slate-400 text-sm ml-2">{displayTitle}</span>
      </div>
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        customStyle={{ margin: 0, padding: '1rem', fontSize: '0.875rem', background: 'transparent' }} // Transparent bg for SyntaxHighlighter
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

// ============================================
// 엑셀 스타일 테이블 컴포넌트
// ============================================
function ExcelTable({ tableData, showRowNumbers = true }) {
  const { headers = [], rows = [] } = tableData;

  return (
    <div className="excel-table-container my-4 overflow-x-auto">
      <table className="excel-table w-full border-collapse text-sm">
        {headers.length > 0 && (
          <thead>
            <tr className="bg-gray-700">
              {showRowNumbers && <th className="border border-gray-600 px-3 py-2 text-center w-10" />}
              {headers.map((h, i) => (
                <th key={i} className="border border-gray-600 px-3 py-2 text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-800/50">
              {showRowNumbers && (
                <th className="border border-gray-600 px-3 py-2 text-center bg-gray-700 font-semibold">
                  {rowIdx + 1}
                </th>
              )}
              {row.map((cell, cellIdx) => {
                const cellValue = String(cell);
                const isFormula = cellValue.startsWith('=');
                return (
                  <td
                    key={cellIdx}
                    className={`border border-gray-600 px-3 py-2 ${isFormula ? 'text-green-400 font-mono' : ''}`}
                  >
                    {cellValue}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        ```
      </table>
    </div>
  );
}


// ============================================
// 단계별 렌더링
// ============================================
function BlankHintBox({ hints }) {
  if (!hints || hints.length === 0) return null;

  return (
    <div className="blank-hint-box mt-6">
      <div className="flex items-center gap-2 mb-2 text-slate-400 font-medium text-xs uppercase tracking-wider">
        <i className="fas fa-lightbulb text-yellow-500/70"></i>
        <span>Hints</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {hints.map((hint, idx) => (
          <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-md border border-white/5 text-xs">
            <span className="font-mono text-cyan-500">#{hint.blankNum}</span>
            <span className="text-slate-400">{hint.typeHint}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 개념(Concept) 단계 컴포넌트
// ============================================
function ConceptStep({ step, onBack, onNext, canGoBack }) {
  const processedText = useMemo(() => {
    if (!step.text) return '';
    let text = preserveCodeEscapes(step.text);
    text = text.replace(/^[ \t]+(```)/gm, '$1');
    return text;
  }, [step.text]);

  return (
    <div className="concept-step h-full flex flex-col relative overflow-hidden">
      {/* 설명 영역 - 전체 높이 (+하단 여백) */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0 pb-24 text-base leading-relaxed">
        <div className="prose prose-invert max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                const language = match ? match[1] : 'text';

                if (!inline && match) {
                  return (
                    <IdeCodeBlock
                      code={String(children).replace(/\n$/, '')}
                      language={language}
                    />
                  );
                }
                return (
                  <code className="bg-black/30 px-1.5 py-0.5 rounded text-cyan-300" {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {String(processedText)}
          </ReactMarkdown>
        </div>
      </div>

      {/* 하단 컨트롤 - Floating */}
      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
      />
    </div >
  );
}

// ============================================
// Visual Logic 단계 컴포넌트 (Mermaid 다이어그램)
// ============================================
function VisualLogicStep({ step, onBack, onNext, canGoBack }) {
  const mermaidRef = useCallback((node) => {
    if (node && step.mermaid_code && window.mermaid) {
      const mermaidCode = sanitizeMermaidCode(step.mermaid_code);
      node.textContent = mermaidCode;
      node.removeAttribute('data-processed');

      window.mermaid.run({ nodes: [node] }).catch((e) => {
        console.error('[MERMAID] 렌더링 에러:', e);
        try {
          console.error('[MERMAID Error Detail]:', JSON.stringify(e, Object.getOwnPropertyNames(e)));
        } catch (err) { }

        node.innerHTML = `
          <div class="mermaid-error p-6 bg-red-950/40 backdrop-blur-xl rounded-2xl border border-red-500/30 shadow-[0_8px_32px_rgba(239,68,68,0.15)]">
            <div class="flex items-center gap-2 mb-3 text-red-400">
                <i class="fas fa-exclamation-triangle"></i>
                <span class="font-semibold">다이어그램 렌더링 오류</span>
            </div>
            <p class="text-red-300/80 text-sm mb-4">표시할 수 없는 다이어그램 형식입니다.</p>
            <pre class="text-xs text-red-200/50 bg-red-950/50 p-4 rounded-xl overflow-auto border border-red-500/10 font-mono">${mermaidCode}</pre>
          </div>
        `;
      });
    }
  }, [step.mermaid_code]);

  const processedText = useMemo(() => {
    if (!step.text) return '';
    let text = preserveCodeEscapes(step.text);
    text = text.replace(/^[ \t]+(```)/gm, '$1');
    text = text.replace(/\*\*"([^"]+)"\*\*/g, '**$1**');
    return text;
  }, [step.text]);

  return (
    <div className="visual-logic-step h-full flex flex-col overflow-hidden relative">
      <div className="flex-1 overflow-y-auto p-4 min-h-0 pb-24">
        {/* 텍스트 설명 */}
        <div className="prose prose-invert max-w-none mb-6">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(processedText)}
          </ReactMarkdown>
        </div>

        {/* Mermaid 다이어그램 - Clean, Borderless, Glass */}
        {step.mermaid_code && (
          <div className="mermaid-container bg-white/[0.02] rounded-2xl p-6 overflow-x-auto max-h-[60vh] overflow-y-auto relative z-0">
            <div className="flex items-center gap-2 mb-6 px-2 opacity-70">
              <i className="fas fa-project-diagram text-indigo-400 text-xs" />
              <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Concept Flow</span>
            </div>
            <div ref={mermaidRef} className="mermaid flex justify-center py-2" />
          </div>
        )}
      </div>

      {/* 하단 컨트롤 - Floating */}
      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
      />
    </div>
  );
}

// ============================================
// Master Pattern 단계 컴포넌트
// ============================================
function MasterPatternStep({ step, onBack, onNext, canGoBack }) {
  const processedText = useMemo(() => {
    if (!step.text) return '';
    return preserveCodeEscapes(step.text).replace(/^[ \t]+(```)/gm, '$1');
  }, [step.text]);

  const detectedLanguage = useMemo(() => {
    return detectLanguage(step.code_template, step.language);
  }, [step.language, step.code_template]);

  return (
    <div className="master-pattern-step h-full flex flex-col relative overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 min-h-0 pb-24">
        <div className="prose prose-invert max-w-none mb-8">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(processedText)}
          </ReactMarkdown>
        </div>

        {step.code_template && (
          <div className="flex flex-col bg-black/40 backdrop-blur-none rounded-2xl overflow-hidden border-0 shadow-none relative z-10">
            {/* Header: Minimal & Flat */}
            <div className="flex items-center gap-2 px-4 py-3 bg-white/5 border-b border-white/5">
              <i className="fas fa-code text-slate-400 text-sm"></i>
              <span className="text-sm font-bold text-slate-200">핵심 패턴</span>
            </div>

            <div className="relative">
              <IdeCodeBlock
                code={formatCodeString(String(step.code_template))}
                language={detectedLanguage}
              />
            </div>
          </div>
        )}
      </div>

      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
      />
    </div>
  );
}

// ============================================
// Mirror Example 단계 컴포넌트
// ============================================
function MirrorExampleStep({ step, onBack, onNext, canGoBack }) {
  const processedText = useMemo(() => {
    if (!step.text) return '';
    return preserveCodeEscapes(step.text).replace(/^[ \t]+(```)/gm, '$1');
  }, [step.text]);

  const detectedLanguage = useMemo(() => {
    return detectLanguage(step.code_example, step.language);
  }, [step.language, step.code_example]);

  return (
    <div className="mirror-example-step h-full flex flex-col relative overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 min-h-0 pb-24">
        <div className="prose prose-invert max-w-none mb-8">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(processedText)}
          </ReactMarkdown>
        </div>

        {step.code_example && (
          <div className="flex flex-col bg-black/40 backdrop-blur-none rounded-2xl overflow-hidden border-0 shadow-none relative z-10">
            {/* Header: Minimal */}
            <div className="flex items-center gap-2 px-4 py-3 bg-white/5 border-b border-white/5">
              <i className="fas fa-layer-group text-slate-400 text-sm" />
              <span className="text-sm font-bold text-slate-200">응용 예제</span>
            </div>

            <div className="relative">
              <IdeCodeBlock
                code={formatCodeString(String(step.code_example))}
                language={detectedLanguage}
              />
              <div className="p-4 bg-slate-800/30 border-t border-white/5 flex items-start gap-3">
                <i className="fas fa-info-circle text-slate-500 mt-0.5 text-xs" />
                <span className="text-xs text-slate-400 leading-relaxed">
                  위 핵심 패턴이 실제 코드에서 어떻게 변형되어 쓰이는지 <span className="text-slate-200">비교</span>해보세요.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
      />
    </div>
  );
}

// ============================================
// MCQ(객관식) 단계 컴포넌트
// ============================================
function McqStep({ step, onBack, onNext, canGoBack, onAnswer }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [answered, setAnswered] = useState(false);

  const correctIdx = Number(step.correct_answer_index ?? -1);
  const options = Array.isArray(step.options) ? step.options : [];

  const handleOptionClick = (idx) => {
    if (answered) return;

    setSelectedIndex(idx);
    setAnswered(true);

    const isCorrect = idx === correctIdx;

    // 콜백으로 결과 전달
    if (onAnswer) {
      onAnswer(isCorrect, step.feedback);
    }

    // 일정 시간 후 다음으로 이동
    setTimeout(() => {
      onNext();
    }, isCorrect ? 800 : 1500);
  };

  const getOptionClass = (idx) => {
    // Base: Cleaner, sharper, less rounded
    const baseClass = "w-full text-left relative overflow-hidden group p-4 rounded-lg border transition-all duration-200 backdrop-blur-sm";

    // 정답 확인 후
    if (answered) {
      if (idx === correctIdx) {
        return `${baseClass} bg-emerald-500/10 border-emerald-500/50 text-emerald-400 font-medium`;
      }
      if (idx === selectedIndex && idx !== correctIdx) {
        return `${baseClass} bg-red-500/10 border-red-500/50 text-red-400`;
      }
      return `${baseClass} bg-transparent border-white/5 text-slate-600 opacity-50`;
    }

    // 기본 상태
    return `${baseClass} bg-white/5 border-transparent text-slate-300 hover:bg-white/10 hover:text-cyan-100 hover:shadow-[0_0_15px_rgba(34,211,238,0.1)]`;
  };

  const processedText = useMemo(() => {
    if (!step.text) return '';
    return preserveCodeEscapes(step.text);
  }, [step.text]);

  return (
    <div className="mcq-step h-full flex flex-col relative overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 min-h-0 pb-24">
        {/* 문제 설명 */}
        {step.text && (
          <div className="prose prose-invert max-w-none mb-4 text-sm text-slate-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {String(processedText)}
            </ReactMarkdown>
          </div>
        )}

        {/* 코드 스니펫 */}
        {step.code_snippet && (
          <IdeCodeBlock
            code={formatCodeString(String(step.code_snippet))}
            language={detectLanguage(step.code_snippet, step.language)}
          />
        )}

        {/* 테이블 */}
        {step.table && (
          <ExcelTable tableData={step.table} />
        )}

        {/* 질문 */}
        <div className="mb-4 font-semibold text-white">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(step.question || '질문')}
          </ReactMarkdown>
        </div>

        {/* 선택지 */}
        <div className="space-y-2">
          {options.map((opt, idx) => {
            const optText = String(opt);
            const renderMode = step.option_render_mode || 'output';

            let displayText = optText;
            let useMonospace = false;

            if (renderMode === 'literal') {
              // 리터럴 모드: 이스케이프 시퀀스를 그대로 표시 (변환 없음)
              // 백슬래시 n을 시각적으로 보여주기 위해 monospace 사용
              useMonospace = true;
            } else {
              // output 모드 (기본): 이스케이프 시퀀스를 실제 문자로 변환
              const hasNewline = optText.includes('\\n');
              if (hasNewline) {
                displayText = optText.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
                useMonospace = true;
              }
            }

            return (
              <button
                key={idx}
                onClick={() => handleOptionClick(idx)}
                disabled={answered}
                className={`
                  w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-300
                  disabled:cursor-not-allowed
                  ${getOptionClass(idx)}
                `}
              >
                {useMonospace ? (
                  <pre className="font-mono text-sm whitespace-pre-wrap">{displayText}</pre>
                ) : (
                  <span>{displayText}</span>
                )}
              </button>
            );
          })}
        </div>

      </div>

      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
        nextLabel="건너뛰기"
      />
    </div>
  );
}

// ============================================
// ButtonFill (버튼 클릭 빈칸 채우기) 단계 컴포넌트 (IDE 스타일 - Final Polish)
// ============================================
function ButtonFillStep({ step, onBack, onNext, canGoBack, onAnswer }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [filledValues, setFilledValues] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  const options = step.options || [];
  const solution = step.solution || [];
  const language = step.language || 'python';
  const displayCode = step.display_code || '';

  // [Fix] blankCount는 display_code의 ___ 패턴 개수로 계산 (solution.length와 불일치 문제 해결)
  const blankCount = (displayCode.match(/___/g) || []).length || solution.length;

  // 사용 가능한 옵션 (아직 선택되지 않은 것)
  const availableOptions = options.filter(opt => !selectedIds.includes(opt.id));

  // 버튼 클릭 핸들러
  const handleOptionClick = (opt) => {
    if (filledValues.length >= blankCount || isSubmitted) return;
    setSelectedIds([...selectedIds, opt.id]);
    setFilledValues([...filledValues, opt.value]);
  };

  // 제출 핸들러
  const handleSubmit = () => {
    if (filledValues.length !== blankCount) return;

    // 유연한 정답 비교: 따옴표 유형 동일 취급
    const normalizeValue = (val) => {
      if (typeof val !== 'string') return val;
      // 큰따옴표 ↔ 작은따옴표 동일 취급
      if (val === '"' || val === "'") return 'QUOTE';
      if (val === '"""' || val === "'''") return 'TRIPLE_QUOTE';
      return val;
    };

    const normalizedFilled = filledValues.map(normalizeValue);
    const normalizedSolution = solution.map(normalizeValue);

    const correct = JSON.stringify(normalizedFilled) === JSON.stringify(normalizedSolution);
    setIsCorrect(correct);
    setIsSubmitted(true);

    if (!correct) {
      setAttemptCount(prev => prev + 1);
      setTimeout(() => {
        setSelectedIds([]);
        setFilledValues([]);
        setIsSubmitted(false);
        setIsCorrect(null);
      }, 1500);
    } else {
      onAnswer?.({ isCorrect: true, usedHint: showAnswer });
      setTimeout(() => onNext?.(), 1500);
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    setSelectedIds([]);
    setFilledValues([]);
    setIsSubmitted(false);
    setIsCorrect(null);
  };

  // 파일 확장자 매핑
  const getFileExtension = (lang) => {
    const map = { python: 'py', javascript: 'js', html: 'html', css: 'css', jsx: 'jsx', vba: 'bas' };
    return map[lang] || lang;
  };

  // 코드 미리보기 렌더링 (빈칸 표시 포함)
  const renderCodePreview = () => {
    if (!displayCode) {
      // display_code가 없으면 빈칸만 표시
      return (
        <div className="flex flex-wrap items-center justify-start gap-3 font-mono text-base">
          {[...Array(blankCount)].map((_, idx) => {
            const value = filledValues[idx];
            const isFilled = value !== undefined;
            const isActive = !isFilled && idx === filledValues.length;

            return (
              <span
                key={idx}
                className={`
                  relative inline-flex items-center justify-center min-w-[60px] h-[32px] px-3 rounded-md text-sm transition-all duration-200
                  ${isFilled
                    ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                    : 'bg-slate-800 border-b-2 border-slate-600'
                  }
                  ${isActive ? 'border-indigo-400 bg-slate-800 shadow-[0_0_10px_rgba(99,102,241,0.2)]' : ''}
                  ${isSubmitted && isCorrect === true ? '!bg-emerald-500/20 !text-emerald-300 !border-emerald-500/30' : ''}
                  ${isSubmitted && isCorrect === false ? '!bg-red-500/20 !text-red-300 !border-red-500/30' : ''}
                `}
              >
                {isFilled ? value : ''}
                {isActive && !isSubmitted && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="absolute inset-0 flex items-center justify-center text-indigo-400 font-bold"
                  >
                    |
                  </motion.span>
                )}
              </span>
            );
          })}
        </div>
      );
    }

    // display_code 파싱: ___ 를 빈칸으로 대체
    // [Fix] formatCodeString을 먼저 적용하여 이스케이프 시퀀스 처리
    const formattedCode = formatCodeString(displayCode, language);
    const parts = formattedCode.split(/(___)/g);
    let blankIdx = 0;

    return (
      <div className="font-mono text-[15px] leading-relaxed text-slate-300 whitespace-pre-wrap">
        {parts.map((part, i) => {
          if (part === '___') {
            const value = filledValues[blankIdx];
            const isFilled = value !== undefined;
            const isActive = !isFilled && blankIdx === filledValues.length;
            blankIdx++;

            return (
              <span
                key={i}
                className={`
                  relative inline-flex items-center justify-center min-w-[50px] h-[28px] px-2 mx-1 rounded text-sm align-middle transition-all duration-200
                  ${isFilled
                    ? 'text-indigo-300 font-medium bg-indigo-500/10 border border-indigo-500/20'
                    : 'bg-slate-800 border-b-2 border-slate-600'
                  }
                  ${isActive ? 'border-indigo-400 bg-slate-800 shadow-[0_0_8px_rgba(99,102,241,0.3)]' : ''}
                  ${isSubmitted && isCorrect === true ? '!text-emerald-300 !border-emerald-500/50 !bg-emerald-500/10' : ''}
                  ${isSubmitted && isCorrect === false ? '!text-red-300 !border-red-500/50 !bg-red-500/10' : ''}
                `}
              >
                {isFilled ? value : ''}
                {isActive && !isSubmitted && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="absolute inset-0 flex items-center justify-center text-indigo-400 font-light text-lg pb-0.5"
                  >
                    |
                  </motion.span>
                )}
              </span>
            );
          }

          // 키워드 하이라이팅 (Simple)
          const keywords = ['def', 'return', 'class', 'import', 'from', 'if', 'else', 'for', 'while', 'print', 'const', 'let', 'var', 'function', '=>'];
          const isKeyword = keywords.includes(part.trim());
          return (
            <span key={i} className={isKeyword ? "text-purple-400 font-medium" : "text-slate-300"}>
              {part}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full relative">
      <div className="flex-1 overflow-y-auto p-6">
        {/* 헤더 */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-slate-800 flex items-center justify-center border border-slate-700">
            <i className="fas fa-terminal text-indigo-400 text-sm"></i>
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-100">{step.title || '코드 완성하기'}</h2>
            <p className="text-sm text-slate-400 mt-0.5">{step.description || '빈칸에 알맞은 코드를 선택하세요.'}</p>
          </div>
        </div>

        {/* IDE 영역 */}
        <div className={`
          rounded-xl overflow-hidden border mb-6 transition-all duration-300 bg-[#1e1e2e]
          ${isSubmitted && isCorrect === true ? 'border-emerald-500/50 shadow-lg shadow-emerald-500/10' : ''}
          ${isSubmitted && isCorrect === false ? 'border-red-500/50 shadow-lg shadow-red-500/10' : ''}
          ${!isSubmitted ? 'border-slate-700 shadow-xl' : ''}
        `}>
          {/* 헤더 */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-[#252535] border-b border-slate-700/50">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-slate-600/50" />
            </div>
            <span className="text-xs text-slate-500 font-mono ml-2">
              main.{getFileExtension(language)}
            </span>
          </div>

          {/* 코드 영역 */}
          <div className="p-5 min-h-[100px] flex items-center overflow-x-auto">
            <div className="flex gap-4 w-full">
              <div className="flex flex-col gap-1 text-slate-700 text-right select-none font-mono text-[15px]" style={{ minWidth: '1.5em' }}>
                {(displayCode || '').split('\n').map((_, i) => <div key={i}>{i + 1}</div>)}
              </div>
              <div className="flex-1">
                {renderCodePreview()}
              </div>
            </div>
          </div>
        </div>

        {/* 피드백 메시지 */}
        <AnimatePresence>
          {isSubmitted && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`text-center mb-6 text-sm font-bold flex items-center justify-center gap-2 ${isCorrect ? 'text-emerald-400' : 'text-red-400'}`}
            >
              <i className={`fas fa-${isCorrect ? 'check-circle' : 'exclamation-triangle'}`}></i>
              {isCorrect ? '정답입니다!' : '다시 한번 확인해보세요.'}
            </motion.div>
          )}
        </AnimatePresence>

        {/* 선택지 영역 (Light Pills Style) */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Options</p>
            {availableOptions.length > 0 && (
              <span className="text-[10px] text-slate-600 bg-slate-800/50 px-2 py-0.5 rounded-full">
                {filledValues.length + 1}번째 빈칸을 채워주세요
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            {availableOptions.map((opt) => (
              <motion.button
                key={opt.id}
                whileHover={{ scale: 1.05, backgroundColor: '#ffffff' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleOptionClick(opt)}
                disabled={isSubmitted || filledValues.length >= blankCount}
                className={`
                  relative flex items-center justify-center px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-200 shadow-lg
                  ${isSubmitted
                    ? 'bg-slate-800 text-slate-600 cursor-not-allowed shadow-none'
                    : 'bg-slate-200 text-slate-900 border border-transparent hover:shadow-[0_0_15px_rgba(255,255,255,0.15)]'
                  }
                `}
              >
                {opt.value}
              </motion.button>
            ))}
            {availableOptions.length === 0 && !isSubmitted && (
              <div className="w-full flex justify-center py-4">
                <span className="text-sm text-slate-500 flex items-center gap-2 animate-pulse">
                  <i className="fas fa-check"></i> 모든 빈칸을 채웠습니다
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 정답 표시 영역 */}
        {showAnswer && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl"
          >
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-lightbulb text-yellow-400 text-lg" />
              <span className="text-yellow-300 font-semibold text-sm">정답</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {solution.map((value, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="text-slate-500 text-xs mb-1">빈칸 {idx + 1}</span>
                  <span className="px-3 py-2 bg-yellow-500/20 text-yellow-200 rounded-lg border border-yellow-500/40 font-mono text-sm font-semibold">
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-xs mt-3 flex items-center gap-1">
              <i className="fas fa-exclamation-triangle text-yellow-500" />
              정답을 확인했습니다. 직접 빈칸을 채워서 제출해 보세요.
            </p>
          </motion.div>
        )}

        {/* 하단 액션 버튼 */}
        <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-slate-800/50">
          {attemptCount >= 3 && !isSubmitted && !showAnswer && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => setShowAnswer(true)}
              className="mr-auto px-4 py-2.5 rounded-xl text-sm font-bold bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/50 transition-all duration-200 flex items-center gap-2"
            >
              <i className="fas fa-lightbulb" />
              정답 보기
            </motion.button>
          )}
          <button
            onClick={handleReset}
            disabled={filledValues.length === 0 || isSubmitted}
            className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all disabled:opacity-30 disabled:hover:bg-transparent"
          >
            초기화
          </button>
          <button
            onClick={handleSubmit}
            disabled={filledValues.length !== blankCount || isSubmitted}
            className={`
              px-8 py-2.5 rounded-full text-sm font-bold shadow-lg transition-all duration-200 flex items-center gap-2
              ${filledValues.length === blankCount && !isSubmitted
                ? 'bg-green-600 hover:bg-green-500 text-white shadow-green-600/30 hover:shadow-green-600/50 transform hover:-translate-y-0.5'
                : 'bg-slate-800 text-slate-600 cursor-not-allowed'
              }
            `}
          >
            제출하기
            <i className="fas fa-arrow-right text-xs opacity-70"></i>
          </button>
        </div>
      </div>

      <ControlBar
        onBack={onBack}
        onNext={onNext}
        canGoBack={canGoBack}
        nextLabel="건너뛰기"
      />
    </div>
  );
}

// ============================================
// Fill in Blank(빈칸 채우기) 단계 컴포넌트
// ============================================
function FillInBlankStep({ step, onBack, onNext, canGoBack, onGrade, setLastCodeSnapshot }) {
  const [isGrading, setIsGrading] = useState(false);

  const processedText = useMemo(() => {
    if (!step.text) return '';
    return preserveCodeEscapes(step.text).replace(/^[ \t]+(```)/gm, '$1');
  }, [step.text]);

  const blankHints = useMemo(() => {
    return generateBlankHints(step.solution, step.text || '');
  }, [step.solution, step.text]);

  // [New] Editor Ref for external submit
  const editorRef = useRef(null);





  // [Fix] Content Update Logic (Uncontrolled Mode)
  // We use useEffect instead of key-based remounting to ensure reliable updates
  const handleEditorSubmit = async (completedCode) => {
    if (isGrading) return;
    setIsGrading(true);

    try {
      if (onGrade) {
        const result = await onGrade(completedCode, step);
        if (result?.isCorrect) {
          setTimeout(() => onNext(), 800);
        }
      }
    } finally {
      setIsGrading(false);
    }
  };

  const handleCheck = () => {
    if (editorRef.current) {
      const code = editorRef.current.submit(); // Get code from editor
      handleEditorSubmit(code);
    }
  };

  const defaultHtmlSkeleton = `<!DOCTYPE html>
                <html lang="ko">
                  <head>
                    <meta charset="UTF-8">
                      <title>Document</title>
                  </head>
                  <body>
                    <!-- 여기에 코드를 작성해주세요 -->

                  </body>
                </html>`;

  const templateCode = useMemo(() => {
    const lang = (step.language || '').toLowerCase();
    // [Fix] Check alternatives: code, source
    const tmpl = step.code_template || step.code || step.source || '';

    // [DEBUG] Log input data
    console.log('[FillInBlankStep.templateCode] Input:', { lang, tmpl: tmpl?.substring(0, 100), stepKeys: Object.keys(step) });

    // [Fix] 템플릿 로직 개선
    // 1. Regex로 모든 형태의 "코드를 작성해주세요" 주석 감지
    const trivialRegex = /(\/\/|#|<!--).*코드를\s*작성해주세요.*/i;
    // [Fix] Only consider trivial if the template becomes empty after removing the comment
    const isTrivial = !tmpl || tmpl.trim() === '' || tmpl.replace(trivialRegex, '').trim() === '';

    // 2. HTML인데 태그가 하나도 없으면 Trivail로 간주 (AI가 주석만 줄 때 방어)
    const isHtmlWithoutTags = (lang === 'html' || lang === 'htmlmixed') && !tmpl.includes('<');

    // [DEBUG] Log decision
    console.log('[FillInBlankStep.templateCode] Decision:', { isTrivial, isHtmlWithoutTags });

    if (!isTrivial && !isHtmlWithoutTags) return formatCodeString(tmpl, lang);


    // --- Fallback Logic ---
    // 2. 언어별 기본 스켈레톤 제공
    if (lang === 'html' || lang === 'htmlmixed') {
      return `<!DOCTYPE html>
                <html lang="ko">
                  <head>
                    <meta charset="UTF-8">
                      <title>Document</title>
                  </head>
                  <body>
                    <!-- 여기에 코드를 작성해주세요 -->

                  </body>
                </html>`;
    }

    if (lang === 'javascript' || lang === 'js') {
      return `// 여기에 코드를 작성해주세요\n\n`;
    }

    // Default to Python skeleton
    return `# 여기에 코드를 작성해주세요\n\n`;
  }, [step.code_template, step.code, step.source, step.language]);


  // [Fix] Track current code for dynamic language detection
  const [currentCode, setCurrentCode] = useState(templateCode);

  // Update currentCode when templateCode changes
  useEffect(() => {
    setCurrentCode(templateCode);
  }, [templateCode]);

  const detectedLanguage = useMemo(() => {
    // [Fix] Use unified detection with current code (including user input)
    return detectLanguage(currentCode, step.language);
  }, [step.language, currentCode]);

  // [Fix] 템플릿 변경 시 에디터 강제 업데이트
  useEffect(() => {
    if (editorRef.current && editorRef.current.setCode) {
      editorRef.current.setCode(templateCode);
    }
  }, [templateCode]);

  const handleEditorChange = (newCode) => {
    setCurrentCode(newCode);
    if (step.onCodeChange) step.onCodeChange(newCode);
  };

  return (
    <div className="fill-in-blank-step h-full flex flex-col relative overflow-hidden min-h-0">
      {/* 설명 영역 (40%) */}
      <div className="h-2/5 overflow-y-auto p-4 border-b border-white/5 bg-transparent pb-12">
        <div className="prose prose-invert max-w-none text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(processedText)}
          </ReactMarkdown>
        </div>

        {/* 빈칸 힌트 */}
        <BlankHintBox hints={blankHints} />

        {/* 테이블 */}
        {step.table && <ExcelTable tableData={step.table} />}
      </div>

      {/* 코드 에디터 영역 (60%) */}
      <div className="flex-1 grid grid-rows-[1fr_auto] min-h-0 relative">
        <div className="p-4 flex flex-col min-h-0 overflow-hidden pb-24 relative">
          <FillInBlankEditor
            ref={editorRef}
            template={templateCode}
            language={detectedLanguage}
            onSubmit={handleEditorSubmit}
            onChange={handleEditorChange}
          />
          {/* [New] Execution Loading Overlay (Minimalist Design) - FinalCodeStep과 동일 */}
          <AnimatePresence>
            {isGrading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 flex items-center justify-center bg-[#0B0E14]/60 backdrop-blur-[2px]"
              >
                <div className="flex flex-col items-center gap-4">
                  {/* Minimalist Spinner/Pulse */}
                  <div className="relative w-12 h-12 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border border-white/10"></div>
                    <div className="absolute inset-0 rounded-full border-t-2 border-cyan-400 animate-spin"></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>
                  </div>

                  {/* Sophisticated Text Animation */}
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-medium text-slate-200 tracking-wide animate-pulse">Running Code...</span>
                    <span className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">Analyzing Logic</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Floating Control Bar */}
        <ControlBar
          onBack={onBack}
          onNext={handleCheck}
          canGoBack={canGoBack}
          nextLabel="정답 확인"
          nextIcon="fa-check"
          extraButtons={
            <button
              onClick={onNext}
              className="px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-white/5 rounded-lg transition-colors text-xs font-medium"
            >
              건너뛰기
            </button>
          }
        />
      </div>
    </div>
  );
}

// ============================================
// Final Code(종합 코딩) 단계 컴포넌트
// ============================================
function FinalCodeStep({ step, onBack, onNext, canGoBack, onRunAndGrade, backLabel, skipLabel = '건너뛰기', isLastStep, onReview }) {
  // [Fix] problem_json이 없으면 step 자체를 problem으로 사용 (레슨 복습 모드 대응)
  const problem = step.problem_json || step;
  // [Optimization] Selector를 사용하여 불필요한 리렌더링 방지
  const lastCodeSnapshot = useLunaStore(state => state.lastCodeSnapshot);
  const [isSolved, setIsSolved] = useState(false); // [New] 성공 상태 관리

  const defaultHtmlSkeleton = `<!DOCTYPE html>
                <html lang="ko">
                  <head>
                    <meta charset="UTF-8">
                      <title>Document</title>
                  </head>
                  <body>
                    <!-- 여기에 코드를 작성해주세요 -->

                  </body>
                </html>`;

  const initialCode = useMemo(() => {
    if (lastCodeSnapshot) return lastCodeSnapshot;

    const lang = (problem.language || '').toLowerCase();
    // [Fix] Check alternatives: code, source
    const tmpl = problem.code_template || problem.code || problem.source || '';

    // [Fix] 템플릿 로직 개선
    // 1. Regex로 모든 형태의 "코드를 작성해주세요" 주석 감지
    const trivialRegex = /(\/\/|#|<!--).*코드를\s*작성해주세요.*/i;
    // [Fix] Only consider trivial if the template becomes empty after removing the comment
    const isTrivial = !tmpl || tmpl.trim() === '' || tmpl.replace(trivialRegex, '').trim() === '';

    // 2. HTML인데 태그가 하나도 없으면 Trivail로 간주 (AI가 주석만 줄 때 방어)
    const isHtmlWithoutTags = (lang === 'html' || lang === 'htmlmixed') && !tmpl.includes('<');

    if (!isTrivial && !isHtmlWithoutTags) return formatCodeString(tmpl, lang);


    // --- Fallback Logic ---
    // 2. 언어별 기본 스켈레톤 제공
    if (lang === 'html' || lang === 'htmlmixed') {
      return `<!DOCTYPE html>
                <html lang="ko">
                  <head>
                    <meta charset="UTF-8">
                      <title>Document</title>
                  </head>
                  <body>
                    <!-- 여기에 코드를 작성해주세요 -->

                  </body>
                </html>`;
    }

    if (lang === 'javascript' || lang === 'js') {
      return `// 여기에 코드를 작성해주세요\n\n`;
    }

    // Default to Python skeleton
    return `# 여기에 코드를 작성해주세요\n\n`;
  }, [lastCodeSnapshot, problem.code_template, problem.code, problem.source, problem.language]);

  const [code, setCode] = useState(initialCode);

  const detectedLanguage = useMemo(() => {
    // [Fix] Use unified detection with current code for dynamic JSX promotion
    return detectLanguage(code, problem.language);
  }, [problem.language, code]);

  // [Fix] 초기 코드가 변경되면 에디터 상태도 업데이트 (Skeleton 적용 등)
  useEffect(() => {
    setCode(initialCode);
  }, [initialCode]);

  const [isRunning, setIsRunning] = useState(false);

  const handleLocalRunAndGrade = async () => {
    if (isRunning) return;
    setIsRunning(true);

    try {
      if (onRunAndGrade) {
        // [Change] 결과(성공 여부)를 받아서 처리
        const result = await onRunAndGrade(code, problem);
        if (result && result.isCorrect) {
          setIsSolved(true);
        }
      }
    } finally {
      setIsRunning(false);
    }
  };

  // [Removed] 정답 맞춰도 레슨 패널에서 완료 카드 표시하지 않음
  // 채팅창에 이미 completion_card가 표시되므로, 파이널 코드 화면 유지

  return (
    <div className="final-code-step h-full flex flex-col relative overflow-hidden">
      {/* 문제 설명 영역 (40%) */}
      <div className="h-2/5 overflow-y-auto p-4 border-b border-white/5 bg-transparent pb-12">
        <h3 className="text-lg font-bold text-white mb-2">{problem.title || '문제'}</h3>
        <div className="prose prose-invert max-w-none text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {String(preserveCodeEscapes(problem.description || ''))}
          </ReactMarkdown>
        </div>

        {/* 입출력 예시 */}
        {problem.examples && problem.examples.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-400 mb-2">입출력 예시</h4>
            {problem.examples.map((ex, idx) => (
              <div key={idx} className="bg-gray-800 rounded-lg p-3 mb-2 text-sm">
                <div className="text-cyan-400">입력: <code>{ex.input}</code></div>
                <div className="text-green-400">출력: <code>{ex.output}</code></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 코드 에디터 영역 (60%) */}
      <div className="flex-1 grid grid-rows-[1fr_auto] min-h-0 relative">
        <div className="p-4 flex flex-col min-h-0 overflow-hidden pb-24 relative">
          <MonacoCodeEditor
            initialCode={initialCode}
            language={detectedLanguage}
            onChange={setCode}
            height="100%"
            className="h-full flex flex-col"
          />
          {/* [New] Execution Loading Overlay (Minimalist Design) */}
          <AnimatePresence>
            {isRunning && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 flex items-center justify-center bg-[#0B0E14]/60 backdrop-blur-[2px]"
              >
                <div className="flex flex-col items-center gap-4">
                  {/* Minimalist Spinner/Pulse */}
                  <div className="relative w-12 h-12 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border border-white/10"></div>
                    <div className="absolute inset-0 rounded-full border-t-2 border-cyan-400 animate-spin"></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>
                  </div>

                  {/* Sophisticated Text Animation */}
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-medium text-slate-200 tracking-wide animate-pulse">Running Code...</span>
                    <span className="text-[10px] text-slate-500 font-mono tracking-widest uppercase mt-1">Analyzing Logic</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Floating Control Bar */}
        <ControlBar
          onBack={onBack}
          onNext={handleLocalRunAndGrade} // [Fix] 실행 버튼 연결
          canGoBack={canGoBack}
          backLabel={backLabel}
          nextLabel={isRunning ? '실행 중...' : '실행 및 채점'}
          nextIcon={isRunning ? 'fa-spinner fa-spin' : 'fa-play'}
          isRunning={isRunning}
          extraButtons={
            <button
              onClick={onNext} // [Fix] 건너뛰기는 원래 onNext 사용
              className="px-3 text-slate-500 hover:text-slate-300 hover:bg-white/5 rounded-lg transition-colors text-xs font-medium h-[46px]"
            >
              {skipLabel}
            </button>
          }
        />
      </div>
    </div>
  );
}
// ============================================
// 레슨 완료 오버레이 카드 컴포넌트
// ============================================
function LessonCompleteOverlay({ onNextLesson, onReview, onChallenge, isLastTopic }) {
  return (
    <div className="absolute inset-x-3 inset-y-3 z-50 flex items-center justify-center pointer-events-none">
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        className="bg-gray-900/95 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/50 shadow-2xl max-w-md w-full relative overflow-hidden pointer-events-auto"
      >
        {/* 배경 효과 */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="text-center relative z-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/30"
          >
            <i className="fas fa-check text-2xl text-white" />
          </motion.div>

          <h2 className="text-2xl font-bold text-white mb-2">Lesson Complete!</h2>
          <p className="text-slate-300 mb-6">
            이번 단원을 모두 학습했습니다.<br />
            정말 잘하셨어요! 🎉
          </p>

          <div className="space-y-3">
            <button
              onClick={onNextLesson}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-cyan-500/25 active:scale-95 group"
            >
              <span>{isLastTopic ? '다음 토픽으로 진행' : '다음 레슨으로 넘어가기'}</span>
              <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform" />
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={onReview}
                className="px-4 py-3 bg-gray-800 hover:bg-gray-700 text-slate-300 hover:text-white rounded-xl font-medium transition-all text-sm border border-gray-700 hover:border-gray-600"
              >
                <i className="fas fa-undo mr-2 text-gray-500" />
                개념 복습하기
              </button>
              <button
                onClick={onChallenge}
                className="px-4 py-3 bg-gray-800 hover:bg-gray-700 text-slate-300 hover:text-white rounded-xl font-medium transition-all text-sm border border-gray-700 hover:border-gray-600"
              >
                <i className="fas fa-trophy mr-2 text-yellow-500" />
                도전 과제
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================
// 메인 LessonPanel 컴포넌트
// ============================================
export default function LessonPanel() {
  const {
    currentLessonPlan,
    currentLessonStepIndex,
    setCurrentLessonStepIndex,
    lessonCompleted,
    setLessonCompleted,
    setAppState,
    addChatMessage,
    setLastCodeSnapshot,
    setCurriculumProgress, // Need this for manual update of progress
    setLoading // [Fix] 로딩 상태 제어 함수 추가
  } = useLunaStore();

  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [simulationResult, setSimulationResult] = useState('');

  // [New] State for code execution
  const [isRunning, setIsRunning] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  const { handleReward } = useRewardHandler(); // [Refactor] Use shared reward handler

  /* 
   * [Fix] 데이터 영속성 관리
   * 새 레슨이 로드될 때(currentLessonPlan 변경 시):
   * 1. 이전 코드 스냅샷 초기화 
   * 2. 레슨 인덱스 0으로 초기화 (항상 첫 페이지부터 시작)
   */
  /* 
   * [Fix] 데이터 영속성 관리
   * 새 레슨이 로드될 때(currentLessonPlan 변경 시):
   * 1. 이전 코드 스냅샷 초기화 
   * 2. 레슨 인덱스 0으로 초기화 (항상 첫 페이지부터 시작)
   * 단순 객체 참조 변경이 아니라 실제 레슨 내용(제목 등)이 바뀌었을 때만 리셋
   */
  /* 
   * [Fix] 데이터 영속성 관리
   * useEffect를 통한 리셋 로직 제거: 페이지 새로고침 시 Store에 저장된 인덱스를 유지하기 위함.
   * 새 레슨 로드 시의 인덱스 리셋은 관 생성 시점(useLunaFlow.js)에서 처리함.
   */
  // const prevPlanTitleRef = useRef(null);

  // 현재 단계 정보
  const steps = useMemo(() => {
    // Debug logging
    console.log('[LessonPanel] currentLessonPlan:', currentLessonPlan);
    if (currentLessonPlan && Array.isArray(currentLessonPlan.steps)) {
      console.log('[LessonPanel] found steps:', currentLessonPlan.steps.map((s, i) => `${i}: ${s.type}`));
      return currentLessonPlan.steps;
    }
    console.warn('[LessonPanel] No steps found in plan');
    return [];
  }, [currentLessonPlan]);

  const currentStep = steps[currentLessonStepIndex];
  const canGoBack = currentLessonStepIndex > 0;
  const isLastStep = steps.length > 0 && currentLessonStepIndex >= steps.length - 1;

  // 다음 단계로 이동
  const goNext = useCallback(() => {
    console.log('[LessonPanel] goNext called. Index:', currentLessonStepIndex, 'Total:', steps.length, 'isLast:', isLastStep);

    if (isLastStep) {
      console.log('[LessonPanel] Finishing lesson (isLastStep=true)');
      // 마지막 단계 완료
      setLessonCompleted(true);
      setAppState('CHAPTER_COMPLETE');
      const state = useLunaStore.getState();
      const isLastCurriculumStep = state.currentCurriculum?.curriculum
        ? state.currentStepIndex >= state.currentCurriculum.curriculum.length - 1
        : false;

      addChatMessage({
        role: 'system',
        type: 'completion_card',
        content: 'Lesson Completed',
        isLastLesson: isLastCurriculumStep // [Fix] 상태 고정 (Freeze)
      });
      return;
    }

    console.log('[LessonPanel] Advancing to index:', currentLessonStepIndex + 1);
    setCurrentLessonStepIndex(currentLessonStepIndex + 1);
    setLastCodeSnapshot(null);
  }, [isLastStep, currentLessonStepIndex, steps.length, setCurrentLessonStepIndex, setLessonCompleted, setAppState, setLastCodeSnapshot, addChatMessage]);

  // 이전 단계로 이동
  const goBack = useCallback(() => {
    if (canGoBack) {
      setCurrentLessonStepIndex(currentLessonStepIndex - 1);
      setLastCodeSnapshot(null);
    }
  }, [canGoBack, currentLessonStepIndex, setCurrentLessonStepIndex, setLastCodeSnapshot]);

  // MCQ 답변 처리
  // MCQ 답변 처리
  const handleMcqAnswer = useCallback((isCorrect, feedback) => {
    if (isCorrect) {
      addChatMessage({
        role: 'ai',
        content: feedback ? `정답입니다! 🎉 ${feedback}` : '정답입니다! 👏',
        forceAnim: true,
      });
    } else {
      // [Fix] 오답일 때도 피드백(해설)이 있으면 보여줌
      addChatMessage({
        role: 'ai',
        content: feedback ? `아쉬워요! ${feedback}` : '아쉬워요! 정답을 확인해보세요. 💪',
        forceAnim: true,
      });
    }
  }, [addChatMessage]);

  // 빈칸 채우기 채점 (AI 호출 없음 - 클라이언트 비교만)
  const handleFillInBlankGrade = useCallback(async (code, step) => {
    // [DEBUG] Log input data
    console.log('[handleFillInBlankGrade] Code:', code);
    console.log('[handleFillInBlankGrade] Step:', step);

    // [Phase 1] 프론트엔드 빠른 비교 (AI 호출 없이 즉시 정답 처리)
    if (step.solution) {
      const template = step.code_template || step.code || '';
      const quickResult = quickCompareWithSolution(step.solution, code, template);

      console.log('[handleFillInBlankGrade] Quick compare result:', quickResult);

      if (quickResult.isCorrect) {
        addChatMessage({
          role: 'ai',
          content: '정답입니다! 🎉 잘하셨어요!',
          forceAnim: true
        });

        // 정답 시 자동 진행
        setTimeout(() => {
          goNext();
        }, 1500);

        return { isCorrect: true };
      } else {
        // 오답 시 간단한 메시지 (AI 호출 없음)
        addChatMessage({
          role: 'ai',
          content: '아쉽네요! 다시 한번 생각해보세요. 💪',
          forceAnim: true
        });
        return { isCorrect: false };
      }
    }

    // solution이 없는 경우 (예외 케이스)
    addChatMessage({
      role: 'ai',
      content: '이 문제는 자동 채점이 지원되지 않습니다. 다음 단계로 진행해주세요.'
    });
    return { isCorrect: false };
  }, [addChatMessage, goNext]);

  // 코드 실행 (Master Pattern, etc)
  const handleRunCode = useCallback(async (code) => {
    // setIsRunning and setExecutionResult are not defined in this scope.
    // Assuming they are passed down or available from a parent context.
    // For now, we'll just focus on the Pyodide integration and chat messages.
    // If setIsRunning and setExecutionResult are state setters, they should be defined via useState.
    // Example: const [isRunning, setIsRunning] = useState(false);
    // Example: const [executionResult, setExecutionResult] = useState(null);

    // If setIsRunning and setExecutionResult are not available, this part will cause errors.
    // For the purpose of this edit, I will assume they are available.
    setIsRunning(true);
    setExecutionResult(null);

    // [New] Pyodide Integration
    try {
      // 1. Load Pyodide (if not loaded) and Run Code
      // Note: First run might be slower due to WASM download
      const { runPythonCode } = await import('../../services/pyodide');

      const { stdout, stderr, error } = await runPythonCode(code);

      const output = stdout + (stderr ? `\n[Error]\n${stderr}` : '');
      const isError = !!error || (stderr && stderr.length > 0);

      const resultData = {
        ok: !isError,
        stdout: stdout,
        stderr: stderr,
        truncated: false // Local execution usually doesn't need strict truncation here
      };

      setExecutionResult(resultData); // Assuming setExecutionResult is defined

      // 2. Add to Chat
      addChatMessage({
        role: 'system',
        content: `[실행 결과]\n${output}`,
        type: isError ? 'error' : 'info'
      });

      // 3. Optional: Send result to backend for logging/consultation context?
      // For now, we keep it purely local as requested for security.

    } catch (err) {
      console.error('[Pyodide] Execution failed:', err);
      setExecutionResult({ ok: false, stdout: '', stderr: err.toString() }); // Assuming setExecutionResult is defined
      addChatMessage({
        role: 'system',
        content: `[시스템 오류] ${err.message}`,
        type: 'error'
      });
    } finally {
      setIsRunning(false); // Assuming setIsRunning is defined
    }
  }, [addChatMessage, setIsRunning, setExecutionResult]);


  // 최종 코드 실행 및 채점
  const handleRunAndGrade = useCallback(async (code, problem) => {
    setLastCodeSnapshot(code);
    let executionResult = '';
    const language = (problem.language || 'python').toLowerCase();

    // [Fix] 전역 로딩 상태 표시
    setLoading(true);

    try {
      // 1. Unified Execution (using shared runner service)
      // This eliminates duplication between FinalCodeStep and interactive steps
      const { executeCode } = await import('../../services/runner');
      const runRes = await executeCode(language, code);

      if (runRes.error) {
        // Append error to stdout if it exists, or use error as result
        executionResult = (runRes.stdout || '') + `\n[Error]\n${runRes.error}`;
      } else {
        executionResult = runRes.stdout || '(출력 없음)';
      }

      // 2. Python specific logic: Quick Local Validation
      // If expected_output is provided, we can grade locally without AI
      if (language === 'python' && problem.expected_output) {
        const normalizeOutput = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
        const expectedNorm = normalizeOutput(problem.expected_output);
        const actualNorm = normalizeOutput(executionResult);

        console.log('[handleRunAndGrade] Quick output compare:', { expected: expectedNorm, actual: actualNorm });

        if (expectedNorm === actualNorm || actualNorm.includes(expectedNorm)) {
          // 정답! AI 호출 없이 즉시 처리
          addChatMessage({
            role: 'ai',
            content: '정답입니다! 🎉 출력 결과가 예상과 일치해요!',
            forceAnim: true
          });

          setSimulationResult(executionResult);
          setShowSimulationModal(true);

          // 완료 카드 표시
          const state = useLunaStore.getState();
          const isLastCurriculumStep = state.currentCurriculum?.curriculum
            ? state.currentStepIndex >= state.currentCurriculum.curriculum.length - 1
            : false;

          addChatMessage({
            role: 'system',
            type: 'completion_card',
            content: 'Lesson Completed',
            isLastLesson: isLastCurriculumStep
          });

          setLoading(false);
          return { isCorrect: true };
        }
      }

      // 3. [즉시] 실행 콘솔 모달 오픈 -> [변경] 채점 후로 이동
      // setSimulationResult(executionResult);
      // setShowSimulationModal(true);

      // 4. 채점 및 피드백 (빠른 비교 실패 또는 expected_output 없을 때만 AI 호출)
      const userId = localStorage.getItem('coding_tutor_user_id') || 'test_user';
      const { currentCurriculum, currentStepIndex, currentLessonPlan } = useLunaStore.getState();
      const topic = currentCurriculum?.topic;

      // [신규] 체험 레슨 감지: DEMO_LESSON의 고유 제목으로 판단
      const isDemo = currentLessonPlan?.title === "데이터 담는 상자, 변수";

      // [Fix] 분석 중 텍스트 제거 (이미 실행 중 로딩이 표시되고 있으므로 ... 애니메이션만 유지)
      // setLoading(true); // 이미 위에서 호출됨

      const gradeRes = await api.gradeCode(problem, code, userId, topic, currentStepIndex, isDemo, 'full', executionResult);
      const { ai_judgment, display, reward } = gradeRes || {};


      // HTML의 경우 AI가 렌더링한 결과가 더 정확할 수 있으므로 업데이트 (선택사항)
      // 또는 runCode 에러가 있었더라도 gradeCode가 성공했다면 display output 사용
      console.log('[handleRunAndGrade] Before display override - executionResult:', executionResult?.substring(0, 100));
      console.log('[handleRunAndGrade] display object:', display);

      if (display) {
        if (display.rendered_html) {
          console.log('[handleRunAndGrade] Overriding with display.rendered_html');
          executionResult = display.rendered_html;
        } else if (display.output && (!executionResult || executionResult === '(출력 없음)')) {
          // [Fix] Python: Pyodide 실행 결과가 없을 때만 백엔드 결과 사용
          console.log('[handleRunAndGrade] Fallback to display.output:', display.output?.substring(0, 100));
          executionResult = display.output;
        }
      }

      // [수정] 채점 완료 후 실행 콘솔 모달 오픈
      setSimulationResult(executionResult);
      setShowSimulationModal(true);

      if (ai_judgment) {
        // 채팅창에 피드백 추가 (결과 텍스트 중복 제거)
        addChatMessage({
          role: 'ai',
          content: ai_judgment.feedback || '채점 결과를 확인해보세요.',
          forceAnim: true
        });

        const isPass = ai_judgment.is_correct;

        if (isPass && reward) {
          handleReward(reward);
          // 진척도 업데이트
          const currentProgress = useLunaStore.getState().curriculumProgress || {};
          const newSteps = { ...(currentProgress.steps || {}) };
          newSteps[currentStepIndex] = { completed: true };
          setCurriculumProgress({ ...currentProgress, steps: newSteps });
        }

        if (isPass) {
          // 정답 시: 채팅창에 완료 카드 표시
          const state = useLunaStore.getState();
          const isLastCurriculumStep = state.currentCurriculum?.curriculum
            ? state.currentStepIndex >= state.currentCurriculum.curriculum.length - 1
            : false;

          addChatMessage({
            role: 'system',
            type: 'completion_card',
            content: 'Lesson Completed',
            isLastLesson: isLastCurriculumStep // [Fix] 상태 고정
          });

          // [Fix] 자동 진행(setTimeout) 및 즉시 완료 처리(setLessonCompleted) 제거
          // 성공 상태(isCorrect: true)를 반환하여, 호출하는 쪽(FinalCodeStep)에서 UI 처리를 하도록 위임
          setLoading(false); // [Fix] 로딩 해제 (AI Pass)
          return { isCorrect: true };
        }
      } else {
        addChatMessage({ role: 'ai', content: '채점 결과를 가져오지 못했어요.' });
      }

      setLoading(false);
    } catch (error) {
      console.error('[Flow] Grading failed:', error);
      setLoading(false);
      addChatMessage({
        role: 'ai',
        content: `채점 과정에서 문제가 발생했어요 😢\n\n**${error.message || '알 수 없는 오류'}**`,
        type: 'error'
      });
      // [Fix] 에러 발생 시에도 로컬 실행 결과는 표시
      if (executionResult) {
        setSimulationResult(executionResult);
        setShowSimulationModal(true);
      }
    }

    return { isCorrect: false };

  }, [setLastCodeSnapshot, addChatMessage, setLessonCompleted, setAppState, goNext, handleReward, setCurriculumProgress, steps.length, currentLessonStepIndex]);


  // Sync problemJSON
  useEffect(() => {
    if (currentStep && (currentStep.type === 'final_code' || currentStep.type === 'fill_in_blank')) {
      const problemData = currentStep.problem_json || currentStep;
      useLunaStore.setState({ problemJSON: problemData });
    }
  }, [currentStep]);

  // 수업 플랜이 없으면 안내 메시지
  if (!currentLessonPlan || steps.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400 flex-col gap-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-400 mx-auto mb-4"></div>
          <p>수업 내용을 불러오는 중입니다...</p>
        </div>
        <button
          onClick={() => setAppState('CURRICULUM_GENERATED')}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
        >
          취소하고 돌아가기
        </button>
      </div>
    );
  }

  // 현재 단계가 없으면 에러
  if (!currentStep) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p>단계 정보를 찾을 수 없습니다.</p>
        </div>
      </div>
    );
  }

  // 단계 타입에 따른 컴포넌트 렌더링
  const stepType = String(currentStep.type || '').toLowerCase();

  let content;

  switch (stepType) {
    case 'concept':
    case 'context':
      content = <ConceptStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'visual_logic':
      content = <VisualLogicStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'master_pattern':
      content = <MasterPatternStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'mirror_example':
      content = <MirrorExampleStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'code_fx':
      content = <CodeFxStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'terminal_sim':
      content = <TerminalSimStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'data_grid':
      content = <DataGridStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'safe_runner':
      content = <SafeRunnerStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
      break;
    case 'mcq':
      content = <McqStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} onAnswer={handleMcqAnswer} />;
      break;
    case 'button_fill':
      content = <ButtonFillStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} onAnswer={handleMcqAnswer} />;
      break;
    case 'fill_in_blank':
      content = <FillInBlankStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} onGrade={handleFillInBlankGrade} setLastCodeSnapshot={setLastCodeSnapshot} />;
      break;
    case 'final_code': {
      const isChallenge = currentStep.is_applied_challenge;
      const { currentLessonPlan } = useLunaStore.getState();
      const nextStep = currentLessonPlan?.steps?.[currentLessonStepIndex + 1];
      const nextIsChallenge = nextStep?.is_applied_challenge;

      let backLabel = '이전';
      let skipLabel = '건너뛰기';
      let onBackHandler = goBack;

      if (isChallenge) {
        backLabel = '레슨 복습하기';
        onBackHandler = () => {
          useLunaStore.getState().setLessonStepIndex(0);
          useLunaStore.getState().setLessonCompleted(false);
        };
      } else if (nextIsChallenge) {
        skipLabel = '응용 문제 풀기 👉';
      }

      content = (
        <FinalCodeStep
          key={currentLessonStepIndex}
          step={currentStep}
          onBack={onBackHandler}
          onNext={goNext}
          canGoBack={canGoBack}
          onRunAndGrade={handleRunAndGrade}
          backLabel={backLabel}
          skipLabel={skipLabel}
          isLastStep={isLastStep}
          onReview={() => {
            useLunaStore.getState().setLessonStepIndex(0);
            useLunaStore.getState().setLessonCompleted(false);
          }}
        />
      );
      break;
    }
    default:
      content = <ConceptStep key={currentLessonStepIndex} step={currentStep} onBack={goBack} onNext={goNext} canGoBack={canGoBack} />;
  }

  return (
    <div className="lesson-panel h-full relative flex flex-col overflow-hidden bg-black/40 backdrop-blur-none rounded-2xl border-0 shadow-none">
      {currentStep?.is_applied_challenge && (
        <div className="bg-indigo-900/40 border-b border-indigo-500/30 p-2 text-center text-xs font-bold text-indigo-200 flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/10">
          <i className="fas fa-fire text-orange-500 animate-pulse"></i>
          <span>[응용 문제] 배운 내용을 활용해 새로운 기능을 구현해보세요!</span>
        </div>
      )}
      {content}

      {/* Execution Console Modal */}
      <AnimatePresence>
        {showSimulationModal && (
          <ExecutionConsoleModal
            result={simulationResult}
            language={currentStep?.language || 'python'} // [New] Pass language
            onClose={() => setShowSimulationModal(false)}
          />
        )}
      </AnimatePresence>

      {/* LevelUpOverlay 제거 (이제 App.jsx에서 전역 관리) */}
    </div>
  );
}

// ============================================
// Execution Console Modal (Premium Design)
// ============================================
function ExecutionConsoleModal({ result, language = 'python', onClose }) {
  const isHtmlResult = result && (
    result.trim().toLowerCase().startsWith('<!doctype') ||
    result.trim().toLowerCase().startsWith('<html')
  );

  // [New] 언어별 파일명 및 실행 명령어 매핑
  const getFileInfo = (lang) => {
    const l = (lang || '').toLowerCase();
    switch (l) {
      case 'python': return { file: 'main.py', cmd: 'python main.py' };
      case 'javascript':
      case 'node': return { file: 'app.js', cmd: 'node app.js' };
      case 'html': return { file: 'index.html', cmd: 'Live Server' };
      case 'css': return { file: 'style.css', cmd: 'Rendering...' };
      case 'vba': return { file: 'script.vbs', cmd: 'cscript script.vbs' };
      case 'r': return { file: 'analysis.R', cmd: 'Rscript analysis.R' };
      case 'go': return { file: 'main.go', cmd: 'go run main.go' };
      case 'java': return { file: 'Main.java', cmd: 'java Main' };
      case 'c': return { file: 'main.c', cmd: './main' };
      case 'cpp': return { file: 'main.cpp', cmd: './main' };
      case 'bash':
      case 'sh':
      case 'shell': return { file: 'script.sh', cmd: 'bash script.sh' };
      case 'sql': return { file: 'query.sql', cmd: 'psql -f query.sql' };
      default: return { file: `script.${l}`, cmd: `run ${l}` };
    }
  };

  const fileInfo = getFileInfo(language);

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-6 animate-in fade-in duration-300">
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 10 }}
        className={`relative w-full h-full max-w-4xl max-h-[600px] flex flex-col rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 isolate ${isHtmlResult ? 'bg-slate-100' : 'bg-[#1e1e1e]'
          }`}
      >
        {/* Header Bar */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isHtmlResult ? 'bg-white border-slate-200 text-slate-700' : 'bg-[#252526] border-black/50 text-slate-300'
          }`}>
          <div className="flex items-center gap-3">
            {/* Traffic Lights */}
            <div className="flex gap-1.5">
              <div onClick={onClose} className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 cursor-pointer shadow-sm transition-colors" />
              <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-sm" />
              <div className="w-3 h-3 rounded-full bg-green-500 shadow-sm" />
            </div>

            {/* Title Badge */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-md text-xs font-bold tracking-wide uppercase shadow-inner ${isHtmlResult ? 'bg-slate-100 text-slate-600' : 'bg-black/30 text-indigo-400'
              }`}>
              <i className={`fas ${isHtmlResult ? 'fa-globe' : 'fa-terminal'}`}></i>
              {isHtmlResult ? 'Web Preview' : 'Terminal Output'}
            </div>
          </div>

          <div className="text-xs opacity-50 font-mono">
            {isHtmlResult ? 'localhost:3000' : fileInfo.cmd}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 relative overflow-hidden">
          {isHtmlResult ? (
            <iframe
              srcDoc={result}
              title="Execution Result"
              className="w-full h-full border-none bg-white"
              sandbox="allow-scripts allow-popups"
            />
          ) : (
            <div className="absolute inset-0 p-6 overflow-auto font-mono text-sm leading-relaxed text-slate-300 selection:bg-indigo-500/30">
              {/* Terminal Style Decoration */}
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              <pre className="whitespace-pre-wrap break-all">
                <span className="text-green-500 mr-2">➜</span>
                <span className="text-blue-400">~/project</span>
                <span className="text-slate-500"> git:(main) </span>
                <span className="text-yellow-400">{fileInfo.cmd}</span>
                <br /><br />
                {result || <span className="text-slate-600 italic">( No output )</span>}
              </pre>

              {/* Blinking Cursor */}
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ repeat: Infinity, duration: 0.8 }}
                className="inline-block w-2.5 h-5 bg-slate-500 align-middle ml-1"
              />
            </div>
          )}
        </div>

        {/* Footer info (Optional) */}
        <div className={`px-4 py-2 text-[10px] text-right border-t ${isHtmlResult ? 'bg-slate-50 border-slate-200 text-slate-400' : 'bg-[#252526] border-black/50 text-slate-500'
          }`}>
          LUNA Execution Environment v2.5.0
        </div>

      </motion.div>
    </div>
  );
}
